/** Enhances the theme's native slideshow; scroll, swipe, selection and controls stay in slideshow.js. */
class TwoCardSlideshow extends HTMLElement {
  #abort;
  #resize;
  #visibility;
  #headerMutation;
  #visible = false;
  #openingReady = false;
  #prepareTimer;
  #index = 0;
  #desktop = matchMedia('(min-width: 1024px)');
  #motion = matchMedia('(prefers-reduced-motion: reduce)');

  async connectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    this.#headerMutation?.disconnect();
    const controller = new AbortController();
    this.#abort = controller;
    await customElements.whenDefined('slideshow-component');
    if (this.querySelector('hfl-video')) await customElements.whenDefined('hfl-video');
    if (!this.isConnected || controller.signal.aborted) return;
    const options = { signal: controller.signal };
    this.slideshow = this.querySelector('slideshow-component');
    this.slides = [...this.querySelectorAll('.two-card__slide')];
    if (!this.slideshow || !this.slides.length) return;
    // Theme-block settings are scoped to their block, not section.blocks.
    const controls = this.querySelectorAll('.two-card__controls .slideshow-controls__dots button');
    this.slides.forEach((slide, i) => {
      const label = slide.dataset.navigationLabel || String(i + 1);
      const control = controls[i];
      if (!control) return;
      control.querySelector('.two-card__nav-label').textContent = label;
      control.setAttribute('aria-label', `Show slide ${i + 1}: ${label}`);
    });
    // Disable the theme's optional mouse-drag behavior for this section only.
    // Native horizontal wheel/touch scrolling remains untouched.
    this.addEventListener('mousedown', (event) => {
      if (event.target.closest('slideshow-slides')) event.stopPropagation();
    }, { ...options, capture: true });
    this.addEventListener('slideshow:select', (event) => {
      if (event.target !== this.slideshow) return;
      this.#select(event.detail.index, true);
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.altKey || event.metaKey || event.ctrlKey || event.target.closest('input,textarea,select,[contenteditable]')) return;
      const destinations = { ArrowLeft: this.#index - 1, ArrowRight: this.#index + 1, Home: 0, End: this.slides.length - 1 };
      if (!(event.key in destinations)) return;
      event.preventDefault();
      this.slideshow.select(destinations[event.key], event);
    }, options);
    this.#desktop.addEventListener('change', this.#syncHeader, options);
    this.#motion.addEventListener('change', this.#syncVideos, options);
    document.addEventListener('visibilitychange', this.#syncVideos, options);
    this.#visibility = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#syncVideos();
    });
    this.#visibility.observe(this);
    this.header = document.querySelector('#header-group');
    this.#resize = new ResizeObserver(this.#syncHeader);
    if (this.header) {
      this.#resize.observe(this.header);
      // The mobile header group uses display: contents; measure its sections.
      const observeSections = () => {
        this.#resize.disconnect();
        this.#resize.observe(this.header);
        [...this.header.children].forEach(section => this.#resize.observe(section));
        this.#syncHeader();
      };
      observeSections();
      this.#headerMutation = new MutationObserver(observeSections);
      this.#headerMutation.observe(this.header, { childList: true });
    }
    window.addEventListener('resize', this.#syncHeader, options);
    if (this.dataset.twoCardHome === 'true') {
      window.addEventListener('scroll', this.#syncHeader, { ...options, passive: true });
    }
    this.#select(this.slideshow.current || 0);
    // Give the opening image the connection first, then prepare at most one slide.
    const openingImages = [...this.slides[0].querySelectorAll('img')];
    Promise.allSettled(openingImages.map(image => image.decode())).then(() => {
      if (controller.signal.aborted) return;
      this.#openingReady = true;
      this.#queuePrepare();
    });
    this.addEventListener('progress', this.#queuePrepare, { ...options, capture: true });
    this.addEventListener('canplaythrough', this.#queuePrepare, { ...options, capture: true });
  }

  disconnectedCallback() {
    clearTimeout(this.#prepareTimer);
    this.#prepareTimer = undefined;
    this.#openingReady = false;
    this.#abort?.abort();
    this.#resize?.disconnect();
    this.#headerMutation?.disconnect();
    this.#visibility?.disconnect();
    this.querySelectorAll('video').forEach(video => video.pause());
    this.slides?.forEach(slide => { slide.inert = false; });
    if (this.header) delete document.documentElement.dataset.twoCardHeader;
  }

  #select(index, announce = false) {
    this.#index = Math.max(0, Math.min(index, this.slides.length - 1));
    const active = this.slides[this.#index];
    this.#loadImages(this.#index);
    this.style.setProperty('--two-card-background', active.dataset.slideBackground);
    this.slides.forEach((slide, i) => {
      if (i !== this.#index && slide.contains(document.activeElement)) {
        this.querySelector('slideshow-slides').focus({ preventScroll: true });
      }
      slide.inert = i !== this.#index;
    });
    if (announce) this.querySelector('[data-two-card-status]').textContent = active.getAttribute('aria-label');
    this.#syncVideos();
    this.#syncHeader();
  }

  #syncVideos = () => {
    const visible = this.#visible && !document.hidden;
    this.slides?.forEach((slide, i) => slide.querySelectorAll('hfl-video').forEach(media => {
      media.setActive(i === this.#index && visible);
      if (!visible || (i !== this.#index && i !== this.#index + 1)) media.cancelPreparation();
    }));
    this.#queuePrepare();
  };

  #loadImages(index) {
    this.slides[index]?.querySelectorAll('template[data-two-card-picture]').forEach(template => {
      const picture = template.content.cloneNode(true);
      picture.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      template.replaceWith(picture);
    });
  }

  #queuePrepare = () => {
    if (this.#prepareTimer !== undefined) return;
    this.#prepareTimer = setTimeout(() => {
      this.#prepareTimer = undefined;
      const connection = navigator.connection;
      if (!this.isConnected || !this.#openingReady || !this.#visible || document.hidden || this.#motion.matches || connection?.saveData) return;
      if (['slow-2g', '2g', '3g'].includes(connection?.effectiveType) || (connection?.downlink && connection.downlink < 1.5)) return;
      // Do not take bandwidth from an active video that is still buffering.
      const activeVideos = [...this.slides[this.#index].querySelectorAll('video')];
      if (activeVideos.some(video => video.readyState < 3 || !video.buffered.length || video.buffered.end(video.buffered.length - 1) < Math.min(video.duration, video.currentTime + 3))) return;
      this.#loadImages(this.#index + 1);
      this.slides[this.#index + 1]?.querySelectorAll('hfl-video').forEach(media => media.prepare());
    }, 600);
  };

  #syncHeader = () => {
    const height = this.header ? [...this.header.children].reduce((total, section) => total + section.getBoundingClientRect().height, 0) : 0;
    const overlay = this.#desktop.matches && this.dataset.twoCardHome === 'true';
    // Overlay headers occupy space inside the slide; normal headers sit above it.
    this.style.setProperty('--two-card-outside-header', `${overlay ? 0 : height}px`);
    this.style.setProperty('--two-card-header', `${overlay ? height : 0}px`);
    if (this.dataset.twoCardHome !== 'true') return;
    if (!overlay) {
      delete document.documentElement.dataset.twoCardHeader;
      return;
    }
    document.documentElement.dataset.twoCardHeader = this.getBoundingClientRect().bottom > height ? this.slides[this.#index].dataset.headerTone : 'dark';
  };
}
if (!customElements.get('two-card-slideshow')) customElements.define('two-card-slideshow', TwoCardSlideshow);
